/**
 * Secure Storage Service - IndexedDB Implementation
 * 
 * CRITICAL: This service uses IndexedDB instead of localStorage/sessionStorage
 * to protect against XSS attacks. IndexedDB is not accessible via simple XSS.
 * 
 * Features:
 * - All private keys stored in IndexedDB
 * - Automatic migration from localStorage/sessionStorage
 * - Backward compatibility
 * - Secure async API
 */

const DB_NAME = 'eternavaults_secure_storage';
const DB_VERSION = 1;
const STORE_NAME = 'private_keys';

class SecureStorageService {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Initialize IndexedDB database
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB: ' + request.error));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Store a private key securely in IndexedDB
   * @param {string} key - Storage key identifier
   * @param {string} value - Encrypted private key data
   * @returns {Promise<void>}
   */
  async setItem(key, value) {
    try {
      const db = await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const data = {
          key: key,
          value: value,
          timestamp: Date.now()
        };
        
        const request = store.put(data);
        
        request.onsuccess = () => {
          resolve();
        };
        
        request.onerror = () => {
          reject(new Error('Failed to store in IndexedDB: ' + request.error));
        };
      });
    } catch (error) {
      throw new Error('Secure storage failed: ' + error.message);
    }
  }

  /**
   * Retrieve a private key from IndexedDB
   * @param {string} key - Storage key identifier
   * @returns {Promise<string|null>} Stored value or null if not found
   */
  async getItem(key) {
    try {
      const db = await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);
        
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : null);
        };
        
        request.onerror = () => {
          reject(new Error('Failed to retrieve from IndexedDB: ' + request.error));
        };
      });
    } catch (error) {
      // If IndexedDB fails, return null (don't throw)
      return null;
    }
  }

  /**
   * Remove a private key from IndexedDB
   * @param {string} key - Storage key identifier
   * @returns {Promise<void>}
   */
  async removeItem(key) {
    try {
      const db = await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);
        
        request.onsuccess = () => {
          resolve();
        };
        
        request.onerror = () => {
          reject(new Error('Failed to remove from IndexedDB: ' + request.error));
        };
      });
    } catch (error) {
      throw new Error('Secure storage removal failed: ' + error.message);
    }
  }

  /**
   * Clear all stored keys from IndexedDB
   * @returns {Promise<void>}
   */
  async clear() {
    try {
      const db = await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();
        
        request.onsuccess = () => {
          resolve();
        };
        
        request.onerror = () => {
          reject(new Error('Failed to clear IndexedDB: ' + request.error));
        };
      });
    } catch (error) {
      throw new Error('Secure storage clear failed: ' + error.message);
    }
  }

  /**
   * Migrate data from localStorage to IndexedDB (backward compatibility)
   * @param {string} localStorageKey - Key in localStorage to migrate
   * @param {string} indexedDBKey - Key to use in IndexedDB
   * @returns {Promise<boolean>} True if migration occurred, false if no data to migrate
   */
  async migrateFromLocalStorage(localStorageKey, indexedDBKey) {
    try {
      // Check if already in IndexedDB
      const existing = await this.getItem(indexedDBKey);
      if (existing) {
        return false; // Already migrated
      }

      // Check localStorage
      const localData = localStorage.getItem(localStorageKey);
      if (!localData) {
        return false; // Nothing to migrate
      }

      // Migrate to IndexedDB
      await this.setItem(indexedDBKey, localData);
      
      // Remove from localStorage after successful migration
      localStorage.removeItem(localStorageKey);
      
      return true;
    } catch (error) {
      // If migration fails, keep data in localStorage as fallback
      return false;
    }
  }

  /**
   * Migrate data from sessionStorage to IndexedDB (backward compatibility)
   * @param {string} sessionStorageKey - Key in sessionStorage to migrate
   * @param {string} indexedDBKey - Key to use in IndexedDB
   * @returns {Promise<boolean>} True if migration occurred, false if no data to migrate
   */
  async migrateFromSessionStorage(sessionStorageKey, indexedDBKey) {
    try {
      // Check if already in IndexedDB
      const existing = await this.getItem(indexedDBKey);
      if (existing) {
        return false; // Already migrated
      }

      // Check sessionStorage
      const sessionData = sessionStorage.getItem(sessionStorageKey);
      if (!sessionData) {
        return false; // Nothing to migrate
      }

      // Migrate to IndexedDB
      await this.setItem(indexedDBKey, sessionData);
      
      // Remove from sessionStorage after successful migration
      sessionStorage.removeItem(sessionStorageKey);
      
      return true;
    } catch (error) {
      // If migration fails, keep data in sessionStorage as fallback
      return false;
    }
  }
}

// Export singleton instance
const secureStorage = new SecureStorageService();
export default secureStorage;



